import React, { FC } from 'react'
import { graphql, useFragment } from 'react-relay'

import { Element } from '../Element'
import { Stack } from '../Stack'
import { Text } from '../Text'
import { Icon } from '../Icon'

import { UserPrimaryPhoto } from '../User/UserPrimaryPhoto'
import { UserDefaultPhoto } from '../User/UserDefaultPhoto'

import { EventCardPresentationsSection_event$key } from './__generated__/EventCardPresentationsSection_event.graphql'

export const EventCardPresentationsSection: FC<{
  event: EventCardPresentationsSection_event$key
}> = ({ event }) => {
  const data = useFragment(
    graphql`
      fragment EventCardPresentationsSection_event on Event {
        eventCardPresentations: presentations(
          first: 3
          orderBy: [CREATED_AT_ASC, ID_ASC]
        ) {
          edges {
            node {
              rowId
              presenterFirstName
              presenterLastName

              presenter {
                firstName
                lastName

                ...UserPrimaryPhoto_user
              }
            }

            cursor
          }

          totalCount
        }
      }
    `,
    event
  )

  if (data.eventCardPresentations.edges.length === 0) {
    return null
  }

  if (data.eventCardPresentations.edges.length === 1) {
    return (
      <Stack direction='horizontal' gap={3} alignItems='center'>
        {data.eventCardPresentations.edges.map(({ node, cursor }) => {
          if (node.presenter) {
            return (
              <Stack
                key={cursor}
                gap={3}
                direction='horizontal'
                alignItems='center'
              >
                <Stack>
                  <UserPrimaryPhoto user={node.presenter} size={8} />

                  <MicrophoneIcon />
                </Stack>

                <Text size={4}>
                  {node.presenter.firstName} {node.presenter.lastName}
                </Text>
              </Stack>
            )
          }

          return (
            <Stack
              key={cursor}
              gap={2}
              direction='horizontal'
              alignItems='center'
            >
              <Stack>
                <UserDefaultPhoto stableId={node.rowId} size={8} />

                <MicrophoneIcon />
              </Stack>

              <Text size={4}>
                {node.presenterFirstName} {node.presenterLastName}
              </Text>
            </Stack>
          )
        })}
      </Stack>
    )
  }

  return (
    <Stack direction='horizontal' gap={3} alignItems='center'>
      <Stack direction='horizontal' alignItems='center'>
        {data.eventCardPresentations.edges.map(
          ({ node, cursor }, index, array) => {
            if (array.length === 1) {
              if (node.presenter) {
                return (
                  <Stack key={cursor}>
                    <UserPrimaryPhoto user={node.presenter} size={8} />

                    <MicrophoneIcon />
                  </Stack>
                )
              }

              return (
                <Stack key={cursor}>
                  <UserDefaultPhoto stableId={node.rowId} size={8} />

                  <MicrophoneIcon />
                </Stack>
              )
            }

            if (index === 0) {
              if (node.presenter) {
                return (
                  <UserPrimaryPhoto
                    key={cursor}
                    user={node.presenter}
                    size={8}
                  />
                )
              }

              return (
                <UserDefaultPhoto key={cursor} stableId={node.rowId} size={8} />
              )
            }

            if (index === data.eventCardPresentations.edges.length - 1) {
              if (node.presenter) {
                return (
                  <Element key={cursor} tokens={{ marginLeft: -4 }}>
                    <UserPrimaryPhoto user={node.presenter} size={8} />

                    <MicrophoneIcon />
                  </Element>
                )
              }

              return (
                <Element key={cursor} tokens={{ marginLeft: -4 }}>
                  <UserDefaultPhoto stableId={node.rowId} size={8} />

                  <MicrophoneIcon />
                </Element>
              )
            }

            if (node.presenter) {
              return (
                <Element key={cursor} tokens={{ marginLeft: -4 }}>
                  <UserPrimaryPhoto user={node.presenter} size={8} />
                </Element>
              )
            }

            return (
              <Element key={cursor} tokens={{ marginLeft: -4 }}>
                <UserDefaultPhoto stableId={node.rowId} size={8} />
              </Element>
            )
          }
        )}
      </Stack>

      <Text size={4}>
        {data.eventCardPresentations.totalCount} Presentations
      </Text>
    </Stack>
  )
}

const MicrophoneIcon: FC = () => {
  return (
    <Element
      tokens={{
        width: 5.5,
        height: 5.5,
        alignItems: 'center',
        justifyContent: 'center',
        color: 'grey.0',
        borderColor: 'grey.0',
        borderWidth: 0.25,
        top: 4.5,
        left: 4.5,
      }}
      style={{
        borderRadius: '50%',
        background:
          'linear-gradient(0deg, #5533EB 28.13%, rgba(85, 51, 235, 0) 159.37%), #FFFFFF',
        boxShadow: 'inset 0px 0px 0px 3px rgba(27, 19, 64, 0.2)',
        position: 'absolute',
      }}
    >
      <Icon name='mic-on-filled' variant='presenterIcon' size={4} />
    </Element>
  )
}
